import { useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Button } from '../../components/ui/Button'
import { Input } from '../../components/ui/Input'
import { CaptchaGate, type CaptchaHandle } from '../../components/security/CaptchaGate'
import { useAuthStore } from '../../stores/authStore'
import { Eye, EyeOff } from 'lucide-react'
import logo from '../../assets/logo/logo.png'
import { showPreferences } from '../../utils/cookieConsentConfig'
import { isCaptchaSatisfied } from '../../utils/captcha'

export default function Login() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [captchaToken, setCaptchaToken] = useState<string | null>(null)
  const captchaRef = useRef<CaptchaHandle>(null)

  const { signIn } = useAuthStore()
  const navigate = useNavigate()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!isCaptchaSatisfied(captchaToken)) {
      setError('Poczekaj na zakończenie weryfikacji antybotowej')
      return
    }

    setLoading(true)
    setError('')

    try {
      await signIn(email, password, captchaToken)
      navigate('/calendar')
    } catch (err: any) {
      setError(err.message || 'Błąd logowania')
      captchaRef.current?.reset()
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/20 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo Header */}
        <div className="text-center mb-8">
          <Link to="/" className="relative inline-flex items-center justify-center mb-2" title="Wróć na stronę główną">
            <img src={logo} alt="2domy.pl" className="h-12 w-12 rounded-md shadow-sm" />
          </Link>
          <h1 className="hero-title text-3xl font-bold bg-gradient-to-r from-blue-600 via-purple-600 to-blue-800 bg-clip-text text-transparent">
            <Link to="/" title="Wróć na stronę główną">2domy.pl</Link>
          </h1>
          <p className="text-slate-600 mt-2">
            Zarządzaj harmonogramem opieki nad dzieckiem
          </p>
        </div>

        {/* Login Form */}
        <div className="card-glass p-8">
          <div className="text-center mb-6">
            <h2 className="text-xl font-semibold text-slate-800">Zaloguj się</h2>
            <p className="text-sm text-slate-600 mt-1">
              Wprowadź swoje dane aby kontynuować
            </p>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-1">
                Email
              </label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="twoj@email.com"
                required
                className="w-full bg-white border-slate-200 text-slate-900 placeholder-slate-400 focus:border-blue-400 focus:ring-2 focus:ring-blue-400 focus:ring-opacity-20"
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-slate-700 mb-1">
                Hasło
              </label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Wprowadź hasło"
                  required
                  className="w-full pr-10 bg-white border-slate-200 text-slate-900 placeholder-slate-400 focus:border-blue-400 focus:ring-2 focus:ring-blue-400 focus:ring-opacity-20"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <CaptchaGate ref={captchaRef} onToken={setCaptchaToken} action="login" />

            <Button
              type="submit"
              disabled={loading}
              className="w-full neon-button"
            >
              {loading ? 'Logowanie...' : 'Zaloguj się'}
            </Button>
          </form>

          <div className="mt-4 text-center">
            <Link
              to="/auth/forgot-password"
              className="text-sm text-blue-600 hover:text-blue-700"
            >
              Nie pamiętasz hasła?
            </Link>
          </div>

          <div className="mt-6 text-center">
            <p className="text-sm text-slate-600">
              Nie masz konta?{' '}
              <Link
                to="/auth/register"
                className="text-blue-600 hover:text-blue-700 font-medium"
              >
                Zarejestruj się
              </Link>
            </p>
          </div>



          {/* Privacy Links */}
          <div className="mt-4 text-center text-xs text-gray-400 space-x-4">
            <button
              className="hover:underline bg-transparent border-0 p-0 text-inherit cursor-pointer"
              onClick={(e) => {
                e.preventDefault()
                showPreferences()
              }}
            >
              Preferencje zgody
            </button>
            <span>•</span>
            <Link to="/polityka-prywatnosci" className="hover:underline">Polityka prywatności</Link>
          </div>
        </div>
      </div>
    </div>
  )
} 